import React, { useEffect, useMemo, useState } from "react";
import { Input, Modal } from "antd";
import Card from "../components/ui/Card";
import Tabs from "../components/ui/Tabs";
import IntentCard from "../components/ui/IntentCard";
import AppLoader from "../components/ui/AppLoader";
import { SelectField } from "../components/ui/Field";
import {
  getOrganizationApiEndpointsByOrgId,
  updateOrganizationApiEndpointByOrgId,
} from "../api/flowWiseOrganizationIntentApi/organizationsIntents";
import { getOrgId } from "../utils/authHelpers";
import { mapApiEndpointToIntent } from "../utils/intents/intentMappers";
import { formatShortDateTime } from "../utils/format";

const DEFAULT_ORG_ID = "+61340541111";

function isValidEndpointUrl(value) {
  const url = String(value || "").trim();
  if (!url) return false;
  if (url.startsWith("/")) return true;
  return /^https?:\/\/.+/i.test(url);
}

export function IntentsPage({ notify, setCurrentPage }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [intents, setIntents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [methodFilter, setMethodFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [updatingById, setUpdatingById] = useState({});

  const organizationId = getOrgId() || DEFAULT_ORG_ID;

  useEffect(() => {
    setCurrentPage("/business/intents");
  }, [setCurrentPage]);

  useEffect(() => {
    const fetchIntents = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await getOrganizationApiEndpointsByOrgId(organizationId);
        const apiItems = Array.isArray(response) ? response : [];
        const mapped = apiItems.map(mapApiEndpointToIntent);
        setIntents(mapped);

        notify?.({
          tone: "success",
          title: "Intents synced",
          description: `${mapped.length} intents loaded from API.`,
        });
      } catch (err) {
        const message = err?.response?.data?.message || err?.message || "Failed to load intents";
        setError(message);
        setIntents([]);
        notify?.({
          tone: "danger",
          title: "Failed to fetch intents",
          description: message,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchIntents();
  }, [notify, organizationId]);

  const methodTabs = useMemo(() => {
    const methods = Array.from(new Set(intents.map((item) => item.method))).sort();
    return [{ key: "all", label: "All Methods" }].concat(
      methods.map((method) => ({ key: method, label: method })),
    );
  }, [intents]);

  const filteredIntents = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return intents.filter((intent) => {
      if (methodFilter !== "all" && intent.method !== methodFilter) return false;
      if (statusFilter === "active" && !intent.isActive) return false;
      if (statusFilter === "disabled" && intent.isActive) return false;
      if (!term) return true;

      return (
        intent.name.toLowerCase().includes(term) ||
        intent.description.toLowerCase().includes(term) ||
        intent.endpointUrl.toLowerCase().includes(term)
      );
    });
  }, [intents, methodFilter, searchTerm, statusFilter]);

  const activeCount = useMemo(
    () => intents.filter((item) => item.isActive).length,
    [intents],
  );

  const methodCount = useMemo(
    () => new Set(intents.map((item) => item.method)).size,
    [intents],
  );

  const handleUpdateEndpoint = async (intent, nextEndpointUrl) => {
    const sanitizedEndpointUrl = String(nextEndpointUrl || "").trim();
    if (!sanitizedEndpointUrl) {
      notify?.({
        tone: "warning",
        title: "Invalid URL",
        description: "Endpoint URL cannot be empty.",
      });
      return;
    }
    if (!isValidEndpointUrl(sanitizedEndpointUrl)) {
      notify?.({
        tone: "warning",
        title: "Invalid endpoint URL",
        description:
          "Use a valid absolute URL (https://...) or a relative API path starting with '/'.",
      });
      return;
    }
    if (sanitizedEndpointUrl === intent.endpointUrl) {
      notify?.({
        tone: "info",
        title: "No changes detected",
        description: "Endpoint URL is unchanged.",
      });
      return;
    }

    const confirmed = await new Promise((resolve) => {
      Modal.confirm({
        title: "Update endpoint URL?",
        className: "intent-update-modal",
        rootClassName: "intent-update-modal",
        width: 700,
        content: (
          <div className="intent-update-modal-body">
            <div className="intent-update-modal-warning">
              If this URL is incorrect, agent functionality can break for this intent.
              Only save verified and reachable endpoints.
            </div>

            <div className="intent-update-modal-meta">
              <div className="intent-update-modal-meta-row">
                <span className="intent-update-modal-label">Intent Name</span>
                <span className="intent-update-modal-value">{intent.name}</span>
              </div>
              <div className="intent-update-modal-meta-row">
                <span className="intent-update-modal-label">Method</span>
                <span className="intent-update-modal-value">{intent.method}</span>
              </div>
              <div className="intent-update-modal-meta-row">
                <span className="intent-update-modal-label">Status</span>
                <span className="intent-update-modal-value">{intent.status}</span>
              </div>
            </div>

            <div className="intent-update-modal-url-compare">
              <div className="intent-update-modal-url-block is-old">
                <div className="intent-update-modal-label">Previous URL</div>
                <div className="intent-update-modal-url">{intent.endpointUrl}</div>
              </div>
              <div className="intent-update-modal-url-block is-new">
                <div className="intent-update-modal-label">Updated URL</div>
                <div className="intent-update-modal-url">{sanitizedEndpointUrl}</div>
              </div>
            </div>
          </div>
        ),
        okText: "Update URL",
        cancelText: "Cancel",
        okButtonProps: { type: "primary" },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) return;

    const payload = {
      id: Number(intent.id),
      org_id: intent.orgId,
      name: intent.name,
      endpoint_url: sanitizedEndpointUrl,
      method: intent.method,
      description: intent.description,
      is_active: intent.isActive,
      meta_data: intent.metaData ?? null,
    };

    try {
      setUpdatingById((prev) => ({ ...prev, [intent.id]: true }));
      const response = await updateOrganizationApiEndpointByOrgId(organizationId, payload);

      const updateFromResponse = Array.isArray(response)
        ? response.find((item) => Number(item.id) === Number(intent.id))
        : response;

      setIntents((prev) =>
        prev.map((item) => {
          if (item.id !== intent.id) return item;
          return {
            ...item,
            endpointUrl: updateFromResponse?.endpoint_url || sanitizedEndpointUrl,
            updatedAt: formatShortDateTime(new Date()),
          };
        }),
      );

      notify?.({
        tone: "success",
        title: "Endpoint updated",
        description: `${intent.name} URL saved successfully.`,
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        "Failed to update endpoint URL.";
      notify?.({
        tone: "danger",
        title: "Update failed",
        description: message,
      });
    } finally {
      setUpdatingById((prev) => ({ ...prev, [intent.id]: false }));
    }
  };

  const isInitialLoading = loading && !intents.length && !error;

  if (isInitialLoading) {
    return (
      <div className="page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Intents</h1>
            <p className="page-subtitle">
              API-backed intents for organization: <strong>{organizationId}</strong>
            </p>
          </div>
        </div>
        <AppLoader
          className="section-gap"
          variant="page"
          label="Loading intents"
          sublabel="Fetching API endpoints for this organization..."
        />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Intents</h1>
          <p className="page-subtitle">
            API-backed intents for organization: <strong>{organizationId}</strong>
          </p>
        </div>
      </div>

      <div className="insight-grid section-gap">
        <div className="insight-tile">
          <div className="insight-label">Total Intents</div>
          <div className="insight-value">{intents.length}</div>
        </div>
        <div className="insight-tile">
          <div className="insight-label">Active</div>
          <div className="insight-value">{activeCount}</div>
        </div>
        <div className="insight-tile">
          <div className="insight-label">Methods</div>
          <div className="insight-value">{methodCount}</div>
        </div>
      </div>

      <Card className="section-gap" title="Intent Explorer" subtitle="Search and filter synced intents">
        <div className="form-grid cols-3">
          <div className="field">
            <span className="field-label">Search</span>
            <Input
              className="intents-search-input"
              placeholder="Search by name, endpoint, description..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              allowClear
            />
          </div>

          <SelectField
            label="Status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            options={[
              { label: "All", value: "all" },
              { label: "Active", value: "active" },
              { label: "Disabled", value: "disabled" },
            ]}
          />
        </div>

        <Tabs tabs={methodTabs} activeKey={methodFilter} onChange={setMethodFilter} />
      </Card>

      {loading ? (
        <AppLoader
          className="section-gap"
          variant="panel"
          label="Loading intents"
          sublabel="Fetching API endpoints for this organization..."
        />
      ) : null}

      {error ? (
        <Card className="section-gap" title="Unable to load intents">
          <div className="form-error">{error}</div>
        </Card>
      ) : null}

      {!loading && !error && !filteredIntents.length ? (
        <Card
          className="section-gap"
          title="No intents found"
          subtitle="Try changing filters or search query."
        />
      ) : null}

      {!loading && !error && filteredIntents.length ? (
        <div className="intent-grid section-gap">
          {filteredIntents.map((intent) => (
            <IntentCard
              key={intent.id}
              intent={intent}
              isUpdating={Boolean(updatingById[intent.id])}
              onUpdateEndpoint={handleUpdateEndpoint}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default IntentsPage;
